import type { QueryClient } from "@tanstack/react-query";
import { useQueryClient } from "@tanstack/react-query";
import type { ContextoEuResposta, RespostaImpersonacao } from "@zaphub/types";
import { useNavigate } from "react-router-dom";

import { api, renovarToken } from "../../lib/api";
import { contextoParaSessao, useSessao, type ContaSessao, type UsuarioSessao } from "../../lib/sessao";

/**
 * "Acessar como cliente".
 *
 * O access token vive só em memória (zustand). Ao entrar na conta do cliente o token do
 * SUPERADMIN é SUBSTITUÍDO pelo de impersonação (30 min, sem refresh próprio); por isso guardamos
 * aqui, fora do React, a sessão original. Na volta, a fonte da verdade é o cookie de refresh
 * (devolve um token novo do SUPERADMIN, pois o guardado pode ter expirado); a cópia guardada só
 * serve de reserva se a renovação falhar. Nada disso vai para localStorage.
 */
interface SessaoOriginal {
  accessToken: string | null;
  usuario: UsuarioSessao | null;
  conta: ContaSessao | null;
}

export interface AcessoAtivo {
  original: SessaoOriginal;
  conta: { id: string; nome: string };
  /** ISO — quando o token de impersonação deixa de valer. */
  expiraEm: string;
}

let ativo: AcessoAtivo | null = null;

/** Estado atual da impersonação (null fora dela, ou depois de recarregar a página). */
export function acessoAtivo(): AcessoAtivo | null {
  return ativo;
}

/** Limpa o que o cliente deixou no cache; chaves de `plataforma` são do administrador. */
function esquecerDadosDeCliente(qc: QueryClient): void {
  qc.removeQueries({ predicate: (q) => q.queryKey[0] !== "plataforma" });
}

export async function iniciarImpersonacao(contaId: string, qc: QueryClient): Promise<RespostaImpersonacao> {
  const { data } = await api.post<RespostaImpersonacao>(`/plataforma/contas/${contaId}/acessar`);

  // Capturado DEPOIS da chamada: se o token do admin foi renovado no meio, guardamos o novo.
  const sessao = useSessao.getState();
  const original: SessaoOriginal = { accessToken: sessao.accessToken, usuario: sessao.usuario, conta: sessao.conta };

  sessao.definirToken(data.accessToken);
  try {
    const { data: eu } = await api.get<ContextoEuResposta>("/auth/eu");
    esquecerDadosDeCliente(qc);
    useSessao.getState().definirSessao(contextoParaSessao(eu));
  } catch (erro) {
    // Não conseguiu abrir a conta: volta a ser o SUPERADMIN, sem estado pela metade.
    useSessao.getState().definirToken(original.accessToken);
    throw erro;
  }
  ativo = { original, conta: data.conta, expiraEm: data.expiraEm };
  return data;
}

export type ResultadoEncerramento =
  | { restaurada: true; contaId: string | null }
  | { restaurada: false; contaId: string | null };

/**
 * Encerra o acesso: registra o fim na auditoria (melhor esforço — sair nunca pode ficar preso
 * por causa disso) e devolve a sessão do SUPERADMIN.
 */
export async function encerrarImpersonacao(): Promise<ResultadoEncerramento> {
  const atual = ativo;
  ativo = null;
  try {
    // `contaId` só é lido se o token de impersonação já expirou (aí a chamada é refeita como SUPERADMIN).
    await api.post("/plataforma/impersonacao/encerrar", atual ? { contaId: atual.conta.id } : {});
  } catch {
    /* o fim já está implícito: o token expira sozinho */
  }
  const contaId = atual?.conta.id ?? null;
  const sessao = useSessao.getState();

  const token = await renovarToken();
  if (token) {
    try {
      const { data: eu } = await api.get<ContextoEuResposta>("/auth/eu");
      const nova = contextoParaSessao(eu);
      if (nova.usuario.role === "SUPERADMIN") {
        useSessao.getState().definirSessao(nova);
        return { restaurada: true, contaId };
      }
    } catch {
      /* cai na cópia guardada */
    }
  }
  if (atual?.original.usuario?.role === "SUPERADMIN") {
    const o = atual.original;
    sessao.definirToken(o.accessToken);
    sessao.definirSessao({ usuario: o.usuario, conta: o.conta, impersonadoPor: null });
    return { restaurada: true, contaId };
  }
  // Sem como provar quem é o administrador: sessão encerrada, volta ao login.
  sessao.limpar();
  return { restaurada: false, contaId };
}

/** Hook usado pela tela da conta (iniciar) e pelo botão do banner (encerrar). */
export function useImpersonacao() {
  const qc = useQueryClient();
  const navegar = useNavigate();

  return {
    iniciar: async (contaId: string) => {
      const r = await iniciarImpersonacao(contaId, qc);
      navegar("/");
      return r;
    },
    encerrar: async () => {
      const r = await encerrarImpersonacao();
      if (!r.restaurada) {
        navegar("/entrar", { replace: true });
      } else {
        navegar(r.contaId ? `/plataforma/contas/${r.contaId}` : "/plataforma", { replace: true });
      }
      // Dados do cliente não ficam no cache do administrador.
      setTimeout(() => esquecerDadosDeCliente(qc), 0);
      return r;
    },
  };
}
